<template>
  <RevModalBase
    backdrop-background-color="hi"
    background-color="mid"
    class="!max-h-[calc(100dvh-32px)] rounded-t-lg md:!max-h-dvh md:top-1/2 md:bottom-auto md:left-1/2 md:w-640 md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-b-lg"
    :close-button-label="i18n(translations.modalCloseA11y)"
    has-padding
    :name="modalName"
    :title="
      view === 'country'
        ? i18n(translations.switchCountryTitle)
        : i18n(translations.modalTitle)
    "
    @close="resetView"
  >
    <template v-if="view === 'country'" #beforeTitle>
      <RevButtonIcon
        :aria-label="i18n(translations.switchCountryBackA11y)"
        class="absolute left-0 top-0"
        :icon="IconArrowLeft"
        variant="ghost"
        @click="resetView"
      />
    </template>

    <template #body>
      <PostalCodeForm
        v-if="view === 'postal-code'"
        @error="handlePostalCodeError"
        @submit="handlePostalCodeSubmit"
        @switch-country="view = 'country'"
      />
      <CountrySwitchMenuSearchList v-else />
    </template>

    <template #trigger="{ open }">
      <slot name="trigger" :open>
        <span
          :class="[
            linkSize === 'compact' ? 'caption' : 'body-2',
            'flex items-center',
            boxedIcon ? '' : 'inline-flex gap-4',
          ]"
          data-qa="delivery-postal-code-trigger"
        >
          <span
            v-if="boxedIcon"
            class="-mr-8 flex w-40 shrink-0 items-center justify-center"
          >
            <IconLocationPinFilled class="h-16 w-16" />
          </span>
          <IconLocationPinFilled v-else class="h-16 w-16" />
          <RevLink v-if="!postalCode" @click="open">
            {{ i18n(translations.updateLabel) }}
          </RevLink>
          <FormattedMessage v-else :definition="translations.shipToLabel">
            <template #postalCode>
              <RevLink @click="open">{{ postalCode }}</RevLink>
            </template>
          </FormattedMessage>
        </span>
      </slot>
    </template>
  </RevModalBase>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevLink } from '@ds/components/Link'
import { RevModalBase, closeModal } from '@ds/components/ModalBase'
import { IconArrowLeft } from '@ds/icons/IconArrowLeft'
import { IconLocationPinFilled } from '@ds/icons/IconLocationPinFilled'

import CountrySwitchMenuSearchList from '~/scopes/navigation/components/CountrySwitch/CountrySwitchMenuSearchList.vue'
import { useDeliveryPostalCode } from '~/scopes/shipping/composables/useDeliveryPostalCode/useDeliveryPostalCode'

import translations from './DeliveryPostalCodeModal.translations'
import PostalCodeForm from './PostalCodeForm/PostalCodeForm.vue'

const props = withDefaults(
  defineProps<{
    entryPoint: 'pdp' | 'cart' | 'nav'
    boxedIcon?: boolean
    trackingContext?: 'web' | 'mweb' | 'recap'
    linkSize?: 'default' | 'compact'
  }>(),
  {
    trackingContext: undefined,
    linkSize: 'default',
  },
)

const emit = defineEmits<{
  (e: 'updated'): void
}>()

const i18n = useI18n()
const { postalCode } = useDeliveryPostalCode()
const { closeAllToasts, openSuccessToast } = useTheToast()
const { trackFormSubmit } = useTracking()

const view = ref<'postal-code' | 'country'>('postal-code')

const modalName = computed(() =>
  props.trackingContext
    ? `postalcode_delivery_promise_${props.entryPoint}_${props.trackingContext}`
    : `postalcode_delivery_promise_${props.entryPoint}`,
)

function handlePostalCodeSubmit() {
  closeModal(modalName.value)
  emit('updated')

  trackFormSubmit({
    zone: 'postalcode_modal',
    entryPoint: props.entryPoint,
    status: 'success',
  })
  closeAllToasts()
  openSuccessToast({
    title: i18n(translations.successTitle),
    content: i18n(translations.successDescription),
    duration: 3000,
  })
}

function handlePostalCodeError() {
  trackFormSubmit({
    zone: 'postalcode_modal',
    entryPoint: props.entryPoint,
    status: 'error',
    outcome: 'invalid_format',
  })
}

function resetView() {
  view.value = 'postal-code'
}
</script>
